import React, { useMemo, useState } from "react";
import * as S from "../../styles";
import { cleanMoneyMask } from "utils";
import CardPaymentInfo from "../CardPaymentInfo";
import PaymentCardSuccess from "./PaymentCardSuccess";
import usePayment, { EnumStatusPayment, PaymentMethods } from "hooks/usePayment";
import { CircleNumber, DescriptionTitle, WrapperCardsPayment } from "./styles";

import { EnumStages } from "views/Partner/types";
import { useCheckout } from "../../hooks/useCheckout";
import PixPayment from "./components/PixPayment";
import { useGetCheckoutInfo } from "views/Pix/providers/get-checkout-info.provider";
import RecaptchaModal from "./components/ModalRecaptcha";
import { useInstallments } from "components/PaymentForm/hooks/useInstallment";
import { CreateChargeResponse } from "services/PublicBff/type";
import LoadingPayment from "./components/LoadingPayment";
import { usePaymentNavigation } from "components/PaymentForm/hooks/usePaymentNavigation";
import { toast } from "react-toastify";
import { GetCheckoutInfoResponse } from "views/Pix/providers/dto/get-checkout-info.dto";
import { TOAST_3DS_ID } from "components/PaymentForm/utils/toastStatus";
import { EnumPaymentMethod } from "../FormPayment/types";

interface PaymentSummaryProps {
  route: string;
  stage: EnumStages;
  tokenRecaptcha: string | null;
  setTokenRecaptcha: (token: string | null) => void;
}

const PaymentCheckout: React.FC<PaymentSummaryProps> = ({
  tokenRecaptcha,
  route,
  stage,
  setTokenRecaptcha,
}) => {
  const [paymentSelected, setPaymentSelected] = useState<
    (PaymentMethods & { index: number }) | null
  >(null);
  const [isOpenModal, setIsModalOpen] = useState<boolean>(false);
  const [infoPix, setInfoPix] = useState<CreateChargeResponse | null>(null);

  usePaymentNavigation({ route, stage });
  const { handleInstallmentInfo } = useInstallments();
  const { paymentMethods, setStatusMethod } = usePayment();
  const { isLoading, createCharge } = useCheckout({
    payment: paymentSelected,
    tokenRecaptcha,
    setInfoPix: handleSetInfoPix,
    onExpiredRecaptcha: () => {
      setIsModalOpen(true);
    },
  });

  const { data: checkoutPullingData } = useGetCheckoutInfo(
    infoPix?.checkout_id || "",
    !!infoPix?.checkout_id
  );

  function handleSelectPayment(payment: PaymentMethods, index: number) {
    if (isLoading || payment.status == "success") {
      return;
    }
    const paymentTreated = { ...payment, index };
    setPaymentSelected(paymentTreated);
    createCharge(paymentTreated);
  }

  async function handleRenewToken(token: string | null) {
    if (token) {
      setTokenRecaptcha(token);
      createCharge(paymentSelected!, token);
    }
    setIsModalOpen(false);
  }

  const isStartedPayment = useMemo(() => {
    const haveStarted = paymentMethods.some(
      (payment) => payment.status === EnumStatusPayment.STARTED
    );
    return haveStarted && paymentSelected?.typeMethod !== EnumPaymentMethod.PIX;
  }, [JSON.stringify(paymentMethods)]);

  const paymentSelectedSuccess = useMemo(() => {
    return (
      paymentSelected &&
      paymentMethods[paymentSelected.index]?.status === EnumStatusPayment.SUCCESS &&
      paymentSelected.typeMethod !== EnumPaymentMethod.PIX
    );
  }, [paymentSelected, JSON.stringify(paymentMethods)]);

  const paymentSelectedIsPix = useMemo(() => {
    const isPix = paymentSelected && paymentSelected?.typeMethod === EnumPaymentMethod.PIX;
    return (
      isPix && paymentMethods[paymentSelected?.index]?.status === EnumStatusPayment.STARTED
    );
  }, [paymentSelected]);

  function handleSetInfoPix(data: CreateChargeResponse) {
    setInfoPix(data);
  }

  const chargesObject = useMemo(() => {
    return checkoutPullingData?.charges?.reduce((acc, curr) => {
      return { ...acc, [curr.id_charge]: curr };
    }, {}) as { [key: string]: GetCheckoutInfoResponse["charges"][number] };
  }, [checkoutPullingData]);

  return (
    <>
      <S.PaymentWrapper enable={true}>
        <S.WrapperBox>
          <S.TitleRow>
            <CircleNumber>
              <span style={{ height: "26px" }}>4</span>
            </CircleNumber>
            <S.Title>Pagamento</S.Title>
          </S.TitleRow>
          <DescriptionTitle>Faça os pagamentos</DescriptionTitle>
          <WrapperCardsPayment>
            {paymentMethods?.map((payment: PaymentMethods, index: number) => (
              <CardPaymentInfo
                key={payment.typeMethod}
                type={payment.typeMethod}
                status={payment?.status || EnumStatusPayment.WAITING}
                value={cleanMoneyMask(payment.value)}
                description={handleInstallmentInfo(payment, index)?.description}
                fee={handleInstallmentInfo(payment, index)?.fee_amount}
                {...((!payment?.status ||
                  ["error", "waiting", "started"].includes(payment.status)) && {
                  onClick: () => handleSelectPayment(payment, index),
                })}
                {...((payment.typeMethod === "credit" ||
                  payment.typeMethod === "debit") && {
                  cardNumber: payment?.number,
                })}
              />
            ))}
          </WrapperCardsPayment>
          {isStartedPayment && <LoadingPayment />}
          {paymentSelectedSuccess && <PaymentCardSuccess />}
          {paymentSelectedIsPix && (
            <PixPayment
              pixCode={
                chargesObject?.[
                  paymentSelected?.chargeId as keyof typeof chargesObject
                ]?.pix_qr_code
              }
              status={
                chargesObject?.[
                  paymentSelected?.chargeId as keyof typeof chargesObject
                ]?.id_status
              }
              due_date={
                chargesObject?.[
                  paymentSelected?.chargeId as keyof typeof chargesObject
                ]?.due_date
              }
              handleConsultAgain={() => createCharge(paymentSelected!)}
              onSuccess={() => {
                toast.success("Pagamento realizado com sucesso!", {
                  autoClose: 3000,
                  toastId: TOAST_3DS_ID,
                })
                setStatusMethod(EnumStatusPayment.SUCCESS, paymentSelected!.index)
              }
              }
              onError={(message: string) => {
                toast.error(message);
                setStatusMethod(EnumStatusPayment.ERROR, paymentSelected!.index);
              }}
            />
          )}
        </S.WrapperBox>
      </S.PaymentWrapper>
      <RecaptchaModal
        isModalOpen={isOpenModal}
        setIsModalOpen={setIsModalOpen}
        setRecaptchaToken={handleRenewToken}
      />
    </>
  );
};

export default PaymentCheckout;
